import { useState, useEffect, useMemo } from "react";
import { useFinancialsInstances } from "@/hooks/use-financials-instances";
import { useDimensionsInstances } from "@/hooks/use-dimensions-instances";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Settings, 
  Link as LinkIcon, 
  CheckCircle2, 
  XCircle,
  RefreshCw,
  Database,
  Info,
  Clock,
  AlertCircle,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

interface SyncRecord {
  id: string;
  timestamp: Date;
  entityType: "Stock Items" | "Stock Movements" | "Purchase Orders" | "Suppliers" | "Warehouses";
  operation: "Create" | "Update" | "Delete";
  status: "success" | "failed" | "pending" | "partial";
  recordsProcessed: number;
  recordsTotal: number;
  source: "Access Financials" | "Access Dimensions";
  duration: number;
  errorMessage?: string;
  reference?: string;
}

type SortColumn = "timestamp" | "entityType" | "status" | "recordsProcessed" | "duration";
type SortDirection = "asc" | "desc";

// Generate mock sync records
const generateMockSyncRecords = (): SyncRecord[] => {
  const records: SyncRecord[] = [];
  const now = new Date();
  
  const entityTypes: SyncRecord["entityType"][] = [
    "Stock Items",
    "Stock Movements",
    "Purchase Orders",
    "Suppliers",
    "Warehouses",
  ];
  
  const operations: SyncRecord["operation"][] = ["Create", "Update", "Delete"];
  const statuses: SyncRecord["status"][] = ["success", "success", "success", "success", "failed", "partial"];
  const sources: SyncRecord["source"][] = ["Access Financials", "Access Financials", "Access Financials"];
  
  for (let i = 0; i < 50; i++) {
    const daysAgo = Math.floor(Math.random() * 7);
    const hoursAgo = Math.floor(Math.random() * 24);
    const minutesAgo = Math.floor(Math.random() * 60);
    
    const timestamp = new Date(now);
    timestamp.setDate(timestamp.getDate() - daysAgo);
    timestamp.setHours(timestamp.getHours() - hoursAgo);
    timestamp.setMinutes(timestamp.getMinutes() - minutesAgo);
    
    const entityType = entityTypes[Math.floor(Math.random() * entityTypes.length)];
    const operation = operations[Math.floor(Math.random() * operations.length)];
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const source = sources[Math.floor(Math.random() * sources.length)];
    
    let recordsTotal = 0;
    let recordsProcessed = 0;
    
    switch (entityType) {
      case "Stock Items":
        recordsTotal = Math.floor(Math.random() * 500) + 100;
        break;
      case "Stock Movements":
        recordsTotal = Math.floor(Math.random() * 200) + 50;
        break;
      case "Purchase Orders":
        recordsTotal = Math.floor(Math.random() * 50) + 10;
        break;
      case "Suppliers":
        recordsTotal = Math.floor(Math.random() * 30) + 5;
        break;
      case "Warehouses":
        recordsTotal = Math.floor(Math.random() * 10) + 1;
        break;
    }
    
    if (status === "success") {
      recordsProcessed = recordsTotal;
    } else if (status === "partial") {
      recordsProcessed = Math.floor(recordsTotal * (0.5 + Math.random() * 0.4));
    } else if (status === "failed") {
      recordsProcessed = 0;
    } else {
      recordsProcessed = Math.floor(recordsTotal * Math.random());
    }
    
    const duration = Math.floor(Math.random() * 120) + 5;
    
    records.push({
      id: `SYNC-${String(i + 1).padStart(4, "0")}`,
      timestamp,
      entityType,
      operation,
      status,
      recordsProcessed,
      recordsTotal,
      source,
      duration,
      errorMessage: status === "failed" ? "Connection timeout to ERP system" : 
                    status === "partial" ? "Some records failed validation" : undefined,
      reference: `REF-${Math.random().toString(36).substring(2, 10).toUpperCase()}`,
    });
  }
  
  return records.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
};

function DimensionsDatabaseSelect({
  value,
  onValueChange,
  enabled,
}: {
  value: string;
  onValueChange: (value: string) => void;
  enabled: boolean;
}) {
  const { data: instances, isLoading, isError, refetch } = useDimensionsInstances(enabled);

  // Clear stale localStorage values that don't match any API instance (e.g. old hardcoded "database-1")
  useEffect(() => {
    if (instances && value && !instances.some((i) => i.instanceId === value)) {
      onValueChange("");
      localStorage.removeItem("integration.dimensions.database");
    }
  }, [instances, value, onValueChange]);

  if (isError) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm text-destructive">Failed to load instances</span>
        <Button variant="ghost" size="sm" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger id="dimensions-database">
        <SelectValue
          placeholder={
            isLoading
              ? "Loading instances..."
              : "Select a database instance"
          }
        />
      </SelectTrigger>
      <SelectContent searchable={false}>
        {isLoading && (
          <SelectItem value="__loading" disabled>
            Loading...
          </SelectItem>
        )}
        {instances?.map((instance) => (
          <SelectItem key={instance.instanceId} value={instance.instanceId}>
            {instance.name || `${instance.organisationName || "Organisation"} - Instance ${instance.instanceId.slice(0, 8)}...`}
          </SelectItem>
        ))}
        {!isLoading && !isError && instances?.length === 0 && (
          <SelectItem value="__empty" disabled>
            No instances available
          </SelectItem>
        )}
      </SelectContent>
    </Select>
  );
}

function FinancialsDatabaseSelect({
  value,
  onValueChange,
  enabled,
}: {
  value: string;
  onValueChange: (value: string) => void;
  enabled: boolean;
}) {
  const { data: instances, isLoading, isError, refetch } = useFinancialsInstances(enabled);

  // Clear stale localStorage values that don't match any API instance (e.g. old hardcoded "database-1")
  useEffect(() => {
    if (instances && value && !instances.some((i) => i.instanceId === value)) {
      onValueChange("");
      localStorage.removeItem("integration.financials.database");
    }
  }, [instances, value, onValueChange]);

  if (isError) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm text-destructive">Failed to load instances</span>
        <Button variant="ghost" size="sm" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger id="financials-database">
        <SelectValue
          placeholder={
            isLoading
              ? "Loading instances..."
              : "Select a database instance"
          }
        />
      </SelectTrigger>
      <SelectContent searchable={false}>
        {isLoading && (
          <SelectItem value="__loading" disabled>
            Loading...
          </SelectItem>
        )}
        {instances?.map((instance) => (
          <SelectItem key={instance.instanceId} value={instance.instanceId}>
            {instance.name || `${instance.organisationName} - Instance ${instance.instanceId.slice(0, 8)}...`}
          </SelectItem>
        ))}
        {!isLoading && !isError && instances?.length === 0 && (
          <SelectItem value="__empty" disabled>
            No instances available
          </SelectItem>
        )}
      </SelectContent>
    </Select>
  );
}

export function IntegrationSyncPage() {
  const [financialsEnabled, setFinancialsEnabled] = useState(false);
  const [dimensionsEnabled, setDimensionsEnabled] = useState(false);
  const [financialsDatabase, setFinancialsDatabase] = useState("");
  const [dimensionsDatabase, setDimensionsDatabase] = useState("");
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [moduleToDisable, setModuleToDisable] = useState<"financials" | "dimensions" | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Load settings from localStorage on mount
  useEffect(() => {
    const financials = localStorage.getItem("integration.financials.enabled") === "true";
    const dimensions = localStorage.getItem("integration.dimensions.enabled") === "true";
    const financialsDb = localStorage.getItem("integration.financials.database") || "";
    const dimensionsDb = localStorage.getItem("integration.dimensions.database") || "";
    
    // Ensure mutual exclusivity - if both are somehow enabled, prioritize financials
    if (financials && dimensions) {
      setFinancialsEnabled(true);
      setDimensionsEnabled(false);
      localStorage.setItem("integration.dimensions.enabled", "false");
    } else {
      setFinancialsEnabled(financials);
      setDimensionsEnabled(dimensions);
    }
    
    setFinancialsDatabase(financialsDb);
    setDimensionsDatabase(dimensionsDb);
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    localStorage.setItem("integration.financials.enabled", financialsEnabled.toString());
    localStorage.setItem("integration.dimensions.enabled", dimensionsEnabled.toString());
    localStorage.setItem("integration.financials.database", financialsDatabase);
    localStorage.setItem("integration.dimensions.database", dimensionsDatabase);
    setIsSaving(false);
    toast.success("Integration settings saved successfully");
  };

  const handleTestConnection = async (module: "financials" | "dimensions") => {
    toast.info(`Testing connection to ${module}...`);
    await new Promise((resolve) => setTimeout(resolve, 1500));
    toast.success(`${module} connection test successful`);
  };

  const handleToggleIntegration = (module: "financials" | "dimensions", currentState: boolean) => {
    // If trying to disable (currentState is true), show confirmation dialog
    if (currentState) {
      setModuleToDisable(module);
      setShowDisableDialog(true);
    } else {
      // If enabling, check mutual exclusivity
      if (module === "financials") {
        if (dimensionsEnabled) {
          toast.error("Please disable Dimensions integration first");
          return;
        }
        setFinancialsEnabled(true);
      } else {
        if (financialsEnabled) {
          toast.error("Please disable Financials integration first");
          return;
        }
        setDimensionsEnabled(true);
      }
    }
  };

  const confirmDisableIntegration = () => {
    if (moduleToDisable === "financials") {
      setFinancialsEnabled(false);
      setFinancialsDatabase("");
      localStorage.removeItem("financialsDatabase");
      toast.success("Access Financials integration disabled and disconnected");
    } else if (moduleToDisable === "dimensions") {
      setDimensionsEnabled(false);
      setDimensionsDatabase("");
      localStorage.removeItem("dimensionsDatabase");
      toast.success("Access Dimensions integration disabled and disconnected");
    }
    setShowDisableDialog(false);
    setModuleToDisable(null);
  };

  const cancelDisableIntegration = () => {
    setShowDisableDialog(false);
    setModuleToDisable(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2">
          <h1 className="text-3xl font-bold tracking-tight">Integration</h1>
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
            🟡 Demo Mode
          </Badge>
        </div>
        <p className="text-muted-foreground mt-2">
          Configure integrations with external systems
        </p>
      </div>

      {/* Main Content */}
      <Card>
        <CardHeader>
          <CardTitle>Integration Management</CardTitle>
          <CardDescription>
            Connect to ERP systems
          </CardDescription>
        </CardHeader>
        <CardContent>

            <div className="space-y-6 mt-6">
              {/* Financials Integration */}
              <Card className="border-2">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Database className="h-5 w-5 text-primary" />
                        <h3 className="text-xl font-semibold">Access Financials</h3>
                        {financialsEnabled ? (
                          <Badge className="bg-green-100 text-green-700 border-green-200">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Connected
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <XCircle className="h-3 w-3 mr-1" />
                            Disconnected
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Connect to your ERP's Financials module to sync GL accounts for stock item analysis codes
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Switch
                          id="financials-enabled"
                          checked={financialsEnabled}
                          onCheckedChange={() => handleToggleIntegration("financials", financialsEnabled)}
                          disabled={dimensionsEnabled}
                        />
                        <div>
                          <Label 
                            htmlFor="financials-enabled" 
                            className={dimensionsEnabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}
                          >
                            Enable Financials Integration
                          </Label>
                          <p className="text-xs text-muted-foreground mt-1">
                            {dimensionsEnabled 
                              ? "Disabled - Dimensions integration is active" 
                              : "Sync stock-related data including Sales Orders, Purchase Orders, Customers, Suppliers, and GL Accounts"}
                          </p>
                        </div>
                      </div>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info className="h-4 w-4 text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="max-w-xs">
                              When enabled, stock-related data including Sales Orders, Purchase Orders, Customers, Suppliers, and GL Accounts will be synced from your ERP
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>

                    {financialsEnabled && (
                      <div className="space-y-3 pl-4 border-l-2 border-primary">
                        <div className="space-y-3">
                          <div>
                            <Label htmlFor="financials-database" className="text-sm font-medium mb-2 block">
                              Database Instance
                            </Label>
                            <FinancialsDatabaseSelect
                              value={financialsDatabase}
                              onValueChange={setFinancialsDatabase}
                              enabled={financialsEnabled}
                            />
                          </div>
                        </div>

                        {financialsDatabase && (
                          <>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <Label className="text-xs text-muted-foreground">ERP System</Label>
                                <p className="font-medium">Access Financials</p>
                              </div>
                              <div>
                                <Label className="text-xs text-muted-foreground">Status</Label>
                                <p className="font-medium text-green-600">Active</p>
                              </div>
                            </div>
                            
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="gap-2"
                                onClick={() => handleTestConnection("financials")}
                              >
                                <RefreshCw className="h-4 w-4" />
                                Test Connection
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Dimensions Integration */}
              <Card className="border-2">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <LinkIcon className="h-5 w-5 text-primary" />
                        <h3 className="text-xl font-semibold">Access Dimensions</h3>
                        {dimensionsEnabled ? (
                          <Badge className="bg-green-100 text-green-700 border-green-200">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Connected
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <XCircle className="h-3 w-3 mr-1" />
                            Disconnected
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Connect to your ERP's Dimensions module for advanced analysis and reporting
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Switch
                          id="dimensions-enabled"
                          checked={dimensionsEnabled}
                          onCheckedChange={() => handleToggleIntegration("dimensions", dimensionsEnabled)}
                          disabled={financialsEnabled}
                        />
                        <div>
                          <Label 
                            htmlFor="dimensions-enabled" 
                            className={financialsEnabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}
                          >
                            Enable Dimensions Integration
                          </Label>
                          <p className="text-xs text-muted-foreground mt-1">
                            {financialsEnabled 
                              ? "Disabled - Financials integration is active" 
                              : "Sync dimension data for advanced reporting"}
                          </p>
                        </div>
                      </div>
                    </div>

                    {dimensionsEnabled && (
                      <div className="space-y-3 pl-4 border-l-2 border-primary">
                        <div className="space-y-3">
                          <div>
                            <Label htmlFor="dimensions-database" className="text-sm font-medium mb-2 block">
                              Database Instance
                            </Label>
                            <DimensionsDatabaseSelect
                              value={dimensionsDatabase}
                              onValueChange={setDimensionsDatabase}
                              enabled={dimensionsEnabled}
                            />
                          </div>
                        </div>

                        {dimensionsDatabase && (
                          <>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <Label className="text-xs text-muted-foreground">ERP System</Label>
                                <p className="font-medium">Access Dimensions</p>
                              </div>
                              <div>
                                <Label className="text-xs text-muted-foreground">Status</Label>
                                <p className="font-medium text-green-600">Active</p>
                              </div>
                            </div>
                            
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="gap-2"
                                onClick={() => handleTestConnection("dimensions")}
                              >
                                <RefreshCw className="h-4 w-4" />
                                Test Connection
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Information Card */}
              <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800">
                <CardContent className="pt-6">
                  <div className="flex gap-3">
                    <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                        Integration Information
                      </h3>
                      <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                        <li>• <strong>Only one ERP integration can be enabled at a time</strong> - choose either Financials or Dimensions</li>
                        <li>• When integration is enabled, you must select a database instance to connect to</li>
                        <li>• GL accounts and other data are automatically synced from your connected ERP system</li>
                        <li>• Use the Test Connection button to verify your integration is working correctly</li>
                        <li>• Disabling an integration will disconnect from the database and require reconfiguration</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button onClick={handleSave} disabled={isSaving}>
                  <Settings className="h-4 w-4 mr-2" />
                  {isSaving ? "Saving..." : "Save Settings"}
                </Button>
              </div>
            </div>
        </CardContent>
      </Card>

      {/* Disable Integration Confirmation Dialog */}
      <AlertDialog open={showDisableDialog} onOpenChange={setShowDisableDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable Integration?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to disable the{" "}
              <strong>
                {moduleToDisable === "financials" ? "Access Financials" : "Access Dimensions"}
              </strong>{" "}
              integration? This will disconnect from{" "}
              <strong>
                {moduleToDisable === "financials" ? financialsDatabase : dimensionsDatabase}
              </strong>{" "}
              and you will need to reconfigure the integration to re-enable it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelDisableIntegration}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDisableIntegration} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Disable Integration
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
